/**
 * The agency's onboarding form (boleta de onboarding), built from its definition: the public
 * application page and the dossier in the console render and read it with the same code, so
 * a field the administration adds appears in both without development.
 */
import {
  type Attrs,
  checkboxControl,
  optionTag,
  radioControl,
  selectControl,
  textControl,
} from './controls.ts';

export interface BoletaField {
  key: string;
  label: string;
  type: 'TEXT' | 'NUMBER' | 'DATE' | 'SELECT' | 'YES_NO' | 'DOCUMENT';
  required: boolean;
  order: number;
  section: string;
  options?: { value: string; label: string }[];
  multiple?: boolean;
  format?: 'EMAIL' | 'PHONE' | 'PERSON_NAME' | 'NATIONAL_ID' | 'TAX_ID';
}
export type BoletaAnswers = Record<string, string | number | boolean | string[]>;

export const esc = (s: string) =>
  s.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );

/** What the browser should offer for each check (keyboard, autofill). */
const INPUT: Record<NonNullable<BoletaField['format']>, Attrs> = {
  EMAIL: { type: 'email', autocomplete: 'email' },
  PHONE: { inputmode: 'tel', autocomplete: 'tel' },
  PERSON_NAME: { autocomplete: 'off' },
  NATIONAL_ID: { inputmode: 'numeric', autocomplete: 'off' },
  TAX_ID: { autocomplete: 'off' },
};

const idOf = (prefix: string, key: string) => `${prefix}-${key}`;

/** One field's control, labelled, with its (hidden) error line. */
function control(f: BoletaField, value: unknown, prefix: string) {
  const id = idOf(prefix, f.key);
  const req = f.required && f.type !== 'DOCUMENT';
  const labelHtml = `${esc(f.label)}${req ? ' <span class="req" aria-hidden="true">*</span>' : ''}`;
  const aria: Attrs = {
    'aria-describedby': `${id}-err`,
    'aria-required': req ? 'true' : undefined,
  };
  const err = `<small class="field-error" id="${id}-err" hidden></small>`;
  const str = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  if (f.type === 'YES_NO')
    return `<div class="field">${radioControl(
      {
        id,
        name: id,
        labelHtml,
        'data-bk': f.key,
        value: value === true ? 'yes' : value === false ? 'no' : '',
        class: 'choices',
        ...aria,
      },
      [
        { value: 'yes', labelHtml: 'Sí', attrs: { class: 'check' } },
        { value: 'no', labelHtml: 'No', attrs: { class: 'check' } },
      ],
    )}${err}</div>`;
  if (f.type === 'SELECT' && f.multiple) {
    const picked = Array.isArray(value) ? value : [];
    return `<fieldset class="field choice" id="${id}" aria-describedby="${id}-err"${
      req ? ' aria-required="true"' : ''
    }><legend>${labelHtml}</legend>${(f.options ?? [])
      .map((o) =>
        checkboxControl(
          { class: 'check', value: o.value, 'data-bk': f.key, checked: picked.includes(o.value) },
          esc(o.label),
        ),
      )
      .join('')}${err}</fieldset>`;
  }
  const input =
    f.type === 'SELECT'
      ? selectControl(
          { id, labelHtml, 'data-bk': f.key, ...aria },
          `${optionTag('', 'Elige una opción', value === undefined || value === '')}${(
            f.options ?? []
          )
            .map((o) => optionTag(o.value, o.label, o.value === value))
            .join('')}`,
        )
      : textControl({
          id,
          labelHtml,
          'data-bk': f.key,
          ...aria,
          value: str,
          ...(f.type === 'DATE'
            ? { type: 'date' }
            : f.type === 'NUMBER'
              ? { inputmode: 'decimal', autocomplete: 'off' }
              : f.type === 'DOCUMENT'
                ? { autocomplete: 'off', placeholder: 'Referencia del documento recibido' }
                : f.format
                  ? INPUT[f.format]
                  : {}),
          maxlength: 200,
        });
  return `<div class="field">${input}${err}</div>`;
}

/** The fields grouped by section, in order: one fieldset per section. */
export function boletaFields(fields: BoletaField[], answers: BoletaAnswers, prefix: string) {
  const sections = new Map<string, BoletaField[]>();
  for (const f of [...fields].sort((a, b) => a.order - b.order))
    sections.set(f.section, [...(sections.get(f.section) ?? []), f]);
  return [...sections.entries()]
    .map(
      ([title, list]) =>
        `<fieldset class="boleta-section"><legend>${esc(title)}</legend>${list
          .map((f) => control(f, answers[f.key], prefix))
          .join('')}</fieldset>`,
    )
    .join('');
}

/** Reads the answers from the page (empty ones are left out; the server checks the rest). */
export function readBoleta(root: ParentNode, fields: BoletaField[]): BoletaAnswers {
  const out: BoletaAnswers = {};
  for (const f of fields) {
    const els = [...root.querySelectorAll<HTMLInputElement>(`[data-bk="${CSS.escape(f.key)}"]`)];
    if (!els.length) continue;
    if (f.type === 'YES_NO') {
      // One radio group: «yes», «no», or nothing chosen yet.
      const v = (els[0] as HTMLInputElement).value;
      if (v === 'yes' || v === 'no') out[f.key] = v === 'yes';
    } else if (f.type === 'SELECT' && f.multiple) {
      out[f.key] = els.filter((e) => e.checked).map((e) => e.value);
    } else {
      const v = (els[0] as HTMLInputElement).value.trim();
      if (v) out[f.key] = v;
    }
  }
  return out;
}

/** Shows the server's problems next to each field; focuses the first. Returns how many. */
export function showBoletaErrors(
  root: ParentNode,
  prefix: string,
  errors: { path: string; message: string }[],
): number {
  for (const el of root.querySelectorAll<HTMLElement>('.field-error')) el.hidden = true;
  for (const el of root.querySelectorAll('[aria-invalid]')) el.removeAttribute('aria-invalid');
  let first: HTMLElement | null = null;
  let shown = 0;
  for (const e of errors) {
    const id = idOf(prefix, e.path);
    const msg = root.querySelector<HTMLElement>(`#${CSS.escape(id)}-err`);
    const input = root.querySelector<HTMLElement>(`#${CSS.escape(id)}`);
    if (!msg || !input) continue;
    msg.textContent = e.message;
    msg.hidden = false;
    input.setAttribute('aria-invalid', 'true');
    first ??= input.matches('fieldset, wa-radio-group')
      ? input.querySelector<HTMLElement>('wa-checkbox, wa-radio')
      : input;
    shown++;
  }
  first?.focus();
  return shown;
}
